import {
  cleanRBIMCode,
  isRBIMNotApplicable,
  type RbimCodeCleanResult,
  type RbimCodeQuestionKey,
} from "@/lib/rbimCodeMap";
import {
  getBirthdatePrecisionLabel,
  formatToMMDDYYYY,
  normalizeBirthdateForStorage,
  resolveAgeReferenceDate,
  resolveMemberAge,
  type AgeBasis,
  type BirthdateMetadata,
  type BirthdateResolverInput,
} from "@/modules/add-new-data/lib/age";
import { parseRbimDisplayCodes } from "@/modules/add-new-data/lib/rbim-display-codes";
import type { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import {
  familyLetterToUnitNo,
  getFamilyDisplayName,
  normalizeFamilyLetter,
  normalizeFamilyUnitNo,
} from "@/lib/family-units";

type StringMemberFieldName = {
  [Field in keyof AddNewDataFormValues["members"][number]]-?:
    Exclude<AddNewDataFormValues["members"][number][Field], undefined> extends string ? Field : never;
}[keyof AddNewDataFormValues["members"][number]];
export type MemberFieldName = Exclude<StringMemberFieldName, keyof BirthdateMetadata>;
export type HouseholdQuestionFieldName = keyof AddNewDataFormValues["householdQuestions"];
type DemographicApplicability = "applicable" | "notApplicable";

export type MemberCodedField = {
  field: MemberFieldName;
  questionKey: RbimCodeQuestionKey;
};

export type HouseholdQuestionCodedField = {
  field: HouseholdQuestionFieldName;
  questionKey: RbimCodeQuestionKey;
};

export type CleanedRbimMember = AddNewDataFormValues["members"][number] & {
  computedAge: number | null;
  minimumAgeYears: number | null;
  maximumAgeYears: number | null;
  ageBasis: AgeBasis;
  isAgeApproximate: boolean;
  displayBirthdate: string;
  codedFields: Partial<Record<MemberFieldName, RbimCodeCleanResult>>;
};

export type CleanedRbimHouseholdQuestions = AddNewDataFormValues["householdQuestions"] & {
  codedFields: Partial<Record<HouseholdQuestionFieldName, RbimCodeCleanResult>>;
};

export type CleanedAnswerRecord = {
  question_number: string;
  field: string;
  raw_value: string;
  cleaned_value: string;
  is_applicable: boolean;
  is_auto_filled: boolean;
  validation_status: "valid" | "invalid";
  remarks?: string;
  line_number?: number;
};

export type CleanedAddNewDataValues = Omit<AddNewDataFormValues, "members" | "householdQuestions"> & {
  householdQuestions: CleanedRbimHouseholdQuestions;
  members: CleanedRbimMember[];
  memberAnswers: CleanedAnswerRecord[];
  householdAnswers: CleanedAnswerRecord[];
  encoded: AddNewDataFormValues;
  searchText: string;
};

export const applicableBlankValue = "NONE";
export const notApplicableValue = "99 - N/A";

const selectableNotApplicableFields = new Set<MemberFieldName>([
  "familyPlanningUse",
  "fpMethodSource",
  "intentionToUseFp",

  // Q31: 99 is a legitimate manually selectable response.
  "soloParentStatus",

  // Pages 8-9: preserve manually selected exact code 99
  "durationOfStayCurrentBarangay",

  // Q43A/Q43B: 99 is a legitimate user-selectable response.
  "hasValidCtc",
  "ctcIssuedInBarangay",

  "skillsTrainingInterest",
  "skills",
]);

export function isExplicitSelectableNotApplicableCode(field: MemberFieldName, value: string | undefined) {
  return selectableNotApplicableFields.has(field) && String(value ?? "").trim() === "99";
}

function shouldClearApplicableSkipValue(field: MemberFieldName, value: string | undefined) {
  return isRBIMNotApplicable(value) && !isExplicitSelectableNotApplicableCode(field, value);
}

export type FinalQuestionApplicabilityRule = {
  scope: "member" | "household";
  minAge?: number;
  maxAge?: number;
  sex?: "Female";
  residentTypes?: readonly string[];
};

export const finalQuestionApplicabilityRules = {
  Q1: { scope: "member" },
  Q2: { scope: "member" },
  Q3: { scope: "member" },
  Q4: { scope: "member" },
  Q5: { scope: "member" },
  Q6: { scope: "member" },
  Q7: { scope: "member" },
  Q8: { scope: "member" },
  Q9: { scope: "member" },
  Q10: { scope: "member" },
  Q11: { scope: "member", minAge: 5 },
  Q12: { scope: "member", minAge: 3, maxAge: 24 },
  Q13: { scope: "member", minAge: 3, maxAge: 24 },
  Q14: { scope: "member", minAge: 3, maxAge: 24 },
  Q15: { scope: "member", minAge: 3, maxAge: 24 },
  Q16: { scope: "member", minAge: 15 },
  Q17: { scope: "member", minAge: 15 },
  Q18: { scope: "member", minAge: 15 },
  Q19: { scope: "member", minAge: 15 },
  Q20: { scope: "member", minAge: 0, maxAge: 0 },
  Q21: { scope: "member", minAge: 0, maxAge: 0 },
  Q22: { scope: "member", minAge: 0, maxAge: 0 },
  Q23: { scope: "member", minAge: 10, maxAge: 54, sex: "Female" },
  Q24: { scope: "member", minAge: 10, maxAge: 54, sex: "Female" },
  Q25: { scope: "member", minAge: 10, maxAge: 54, sex: "Female" },
  Q26: { scope: "member", minAge: 10, maxAge: 54, sex: "Female" },
  Q27: { scope: "member" },
  Q28: { scope: "member" },
  Q29: { scope: "member" },
  Q30: { scope: "member" },
  Q31: { scope: "member", minAge: 10 },
  Q32: { scope: "member", minAge: 60 },
  Q33: { scope: "member", minAge: 15 },
  Q34: { scope: "member", minAge: 5 },
  Q35: { scope: "member", minAge: 5 },
  Q36: { scope: "member", minAge: 5 },
  Q37: { scope: "member", minAge: 5 },
  Q38: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q39A: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q39B: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q39C: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q40: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q41A: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q41B: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q41C: { scope: "member", minAge: 5, residentTypes: ["2", "3"] },
  Q42: { scope: "member", minAge: 18, residentTypes: ["2", "3"] },
  Q43A: { scope: "member", minAge: 18 },
  Q43B: { scope: "member", minAge: 18 },
  Q44: { scope: "member", minAge: 15 },
  Q45: { scope: "member", minAge: 15 },
  Q46: { scope: "household" },
  Q47: { scope: "household" },
  Q48: { scope: "household" },
  Q49: { scope: "household" },
  Q50: { scope: "household" },
  Q51: { scope: "household" },
  Q52: { scope: "household" },
  Q53: { scope: "household" },
  Q54A: { scope: "household" },
  Q54B: { scope: "household" },
  Q55: { scope: "household" },
  Q56: { scope: "household" },
  Q57: { scope: "household" },
  Q58: { scope: "household" },
  Q59: { scope: "household" },
  Q60: { scope: "household" },
  Q61: { scope: "household" },
  Q62: { scope: "household" },
} as const satisfies Record<string, FinalQuestionApplicabilityRule>;

export const memberCodedFields = [
  { field: "relationshipToHead", questionKey: "q02_relationship_to_head" },
  { field: "sex", questionKey: "q03_sex" },
  { field: "nationality", questionKey: "q07_nationality" },
  { field: "civilStatus", questionKey: "q08_marital_status" },
  { field: "highestEducation", questionKey: "q11_highest_education" },
  { field: "currentlyEnrolled", questionKey: "q12_currently_enrolled" },
  { field: "schoolLevel", questionKey: "q13_school_level" },
  { field: "reasonNotAttendingSchool", questionKey: "q15_reason_not_attending_school" },
  { field: "sourceOfIncome", questionKey: "q17_source_of_income" },
  { field: "workStatus", questionKey: "q18_work_status" },
  { field: "placeOfWork", questionKey: "q19_place_of_work" },
  { field: "placeOfDelivery", questionKey: "q20_place_of_delivery" },
  { field: "birthAttendant", questionKey: "q21_birth_attendant" },
  { field: "immunization", questionKey: "q22_immunization" },
  { field: "familyPlanningUse", questionKey: "q24_family_planning_use" },
  { field: "fpMethodSource", questionKey: "q25_fp_method_source" },
  { field: "intentionToUseFp", questionKey: "q26_intention_to_use_fp" },
  { field: "healthInsurance", questionKey: "q27_health_insurance" },
  { field: "facilityVisitedPast12Months", questionKey: "q28_facility_visited" },
  { field: "healthFacilityVisitReason", questionKey: "q29_health_facility_visit_reason" },
  { field: "disability", questionKey: "q30_disability" },
  { field: "soloParentStatus", questionKey: "q31_solo_parent" },
  { field: "registeredSeniorCitizen", questionKey: "q32_registered_senior_citizen" },
  { field: "lengthOfStayInBarangay", questionKey: "q34_length_of_stay_barangay" },
  { field: "previousResidenceFiveYears", questionKey: "q35_previous_residence_five_years" },
  { field: "previousResidenceSixMonths", questionKey: "q36_previous_residence_six_months" },
  { field: "residentType", questionKey: "q37_resident_type" },
  { field: "dateOfTransfer", questionKey: "q38_date_of_transfer" },
  { field: "reasonLeavingPreviousResidence1", questionKey: "q39_reason_leaving_previous_residence" },
  { field: "reasonLeavingPreviousResidence2", questionKey: "q39_reason_leaving_previous_residence" },
  { field: "reasonLeavingPreviousResidence3", questionKey: "q39_reason_leaving_previous_residence" },
  { field: "returnToPreviousResidence", questionKey: "q40_return_to_previous_residence" },
  { field: "transferReason1", questionKey: "q41_reason_transferring_barangay" },
  { field: "transferReason2", questionKey: "q41_reason_transferring_barangay" },
  { field: "transferReason3", questionKey: "q41_reason_transferring_barangay" },
  { field: "durationOfStayCurrentBarangay", questionKey: "q42_duration_of_stay_current_barangay" },
  { field: "hasValidCtc", questionKey: "q43a_has_valid_ctc" },
  { field: "ctcIssuedInBarangay", questionKey: "q43b_ctc_issued_in_barangay" },
  { field: "skillsTrainingInterest", questionKey: "q44_skills_development_training" },
  { field: "skills", questionKey: "q45_skills" },
] as const satisfies readonly MemberCodedField[];

export const householdQuestionCodedFields = [
  { field: "housingTenure", questionKey: "q46_housing_unit_tenure" },
  { field: "lotTenure", questionKey: "q47_lot_tenure" },
  { field: "hazardProneArea", questionKey: "q48_hazard_prone_area" },
  { field: "disasterExperiencedPastFiveYears", questionKey: "q49_disaster_experienced_past_5_years" },
  { field: "knowsNearestEvacuationCenter", questionKey: "q50_knows_nearest_evacuation_center" },
  { field: "lightingFuel", questionKey: "q51_fuel_lighting" },
  { field: "cookingFuel", questionKey: "q52_fuel_cooking" },
  { field: "drinkingWaterSource", questionKey: "q53_drinking_water_source" },
  { field: "garbageDisposal", questionKey: "q54a_kitchen_garbage_disposal" },
  { field: "segregatesGarbage", questionKey: "q54b_garbage_segregation" },
  { field: "toiletFacility", questionKey: "q55_toilet_facility_final" },
  { field: "buildingType", questionKey: "q56_building_type" },
  { field: "wallMaterial", questionKey: "q57_wall_material" },
  { field: "femaleDeathPast12Months", questionKey: "q58_female_death_past_12_months" },
  { field: "childDeathPast12Months", questionKey: "q59_child_death_past_12_months" },
  { field: "childDeathSex", questionKey: "q59_child_death_sex" },
] as const satisfies readonly HouseholdQuestionCodedField[];

function cleanRegisteredVoterBarangayValue(
  member: AddNewDataFormValues["members"][number],
  age: number | null,
) {
  if (
    getApplicabilityForQuestion(
      member,
      "registeredVoterBarangay",
      age,
    ) === "notApplicable"
  ) {
    return notApplicableValue;
  }

  /*
   * Q33 barangay names must preserve user-entered spacing.
   * Do not collapse or strip spaces from the stored answer.
   */
  const raw = String(member.registeredVoterBarangay ?? "");

  if (!raw.trim()) {
    return "";
  }

  if (
    /^99\s*-\s*N\/A$/i.test(raw) ||
    /^2\s*-\s*No\s*\|\s*99\s*-\s*N\/A$/i.test(raw)
  ) {
    return "2 - No | 99 - N/A";
  }

  const yesMatch =
    /^1\s*-\s*Yes(?:\s*\|\s*(.*))?$/i.exec(raw);

  if (yesMatch) {
    const barangay = String(yesMatch[1] ?? "");

    return barangay.trim()
      ? `1 - Yes | ${barangay}`
      : "";
  }

  if (
    /^1\s*-\s*Yes\b/i.test(raw) ||
    /^2\s*-\s*No\b/i.test(raw)
  ) {
    return "";
  }

  // Existing legacy Q33 values stored only the barangay.
  return `1 - Yes | ${raw}`;
}
export function cleanAddNewDataValues(values: AddNewDataFormValues): CleanedAddNewDataValues {
  const familyLetter = normalizeFamilyLetter(values.household.family);
  const householdFamilyUnitNo = familyLetterToUnitNo(familyLetter);
  const preserveMemberFamilyUnits = shouldPreserveMemberFamilyUnits(values, householdFamilyUnitNo);
  const ageReferenceDate = resolveAgeReferenceDate(values.interviews, { members: values.members });
  const ageReferenceDateValue = formatToMMDDYYYY(ageReferenceDate);
  const q55InheritedFromFamilyA =
    /^[B-J]$/i.test(familyLetter);

  const householdQuestionsCodedFields = Object.fromEntries(
    householdQuestionCodedFields.map(({ field, questionKey }) => {
      /*
       * Families B-J do not answer Q55 again because the
       * household-level toilet-facility response was already
       * supplied by Family A.
       *
       * Treat the system-generated 99 - N/A as a valid
       * non-applicable result without adding 99 to the normal
       * Family A Q55 dropdown.
       */
      if (
        field === "toiletFacility" &&
        q55InheritedFromFamilyA &&
        isRBIMNotApplicable(
          values.householdQuestions.toiletFacility,
        )
      ) {
        return [
          field,
          {
            code: "99",
            label: notApplicableValue,
          } satisfies RbimCodeCleanResult,
        ];
      }

      return [
        field,
        normalizeHouseholdCodedAnswer(
          questionKey,
          values.householdQuestions[field],
        ),
      ];
    }),
  ) as CleanedRbimHouseholdQuestions["codedFields"];

  const householdQuestions = {
    ...cleanStringRecord(values.householdQuestions),
    codedFields: householdQuestionsCodedFields,
  };

  householdQuestionCodedFields.forEach(({ field }) => {
    const result = householdQuestionsCodedFields[field];
    householdQuestions[field] = result?.error
      ? cleanPlainText(values.householdQuestions[field])
      : (result?.label ?? applicableBlankValue);
  });

  const members = values.members.map((sourceMember) => {
    const sourceFamilyUnitNo = normalizeFamilyUnitNo(sourceMember.familyUnitNo, householdFamilyUnitNo);
    const memberFamilyUnitNo = preserveMemberFamilyUnits ? sourceFamilyUnitNo : householdFamilyUnitNo;
    const member = {
      ...sourceMember,
      familyUnitAssignment: "existing" as const,
      familyUnitNo: memberFamilyUnitNo,
      familyName: preserveMemberFamilyUnits
        ? getFamilyDisplayName(memberFamilyUnitNo, sourceMember.familyName)
        : `Family ${familyLetter}`,
    };
    const resolvedBirthdate = resolveMemberBirthdate(member, ageReferenceDate);
    const age = resolvedBirthdate.ageYears;
    const isReportedAge = resolvedBirthdate.ageBasis === "reported";
    const importedAge = getImportedAgeMetadata(member);
    const codedFields = Object.fromEntries(
      memberCodedFields.map(({ field, questionKey }) => [
        field,
        normalizeCodedAnswer(member, field, questionKey, age),
      ]),
    ) as CleanedRbimMember["codedFields"];
    codedFields.nationality = mergeNationalityDetail(codedFields.nationality, member.nationalityOther);
    codedFields.reasonNotAttendingSchool = mergeOtherSpecifyDetail(
      codedFields.reasonNotAttendingSchool,
      member.reasonNotAttendingSchoolOther,
    );
    codedFields.sourceOfIncome = mergeOtherSpecifyDetail(codedFields.sourceOfIncome, member.sourceOfIncomeOther);
    codedFields.placeOfDelivery = mergeOtherSpecifyDetail(codedFields.placeOfDelivery, member.placeOfDeliveryOther);
    codedFields.birthAttendant = mergeOtherSpecifyDetail(codedFields.birthAttendant, member.birthAttendantOther);
    codedFields.familyPlanningUse = mergeOtherSpecifyDetail(codedFields.familyPlanningUse, member.familyPlanningUseOther);
    codedFields.fpMethodSource = mergeOtherSpecifyDetail(codedFields.fpMethodSource, member.fpMethodSourceOther);
    codedFields.intentionToUseFp = mergeIntentionToUseFpDetail(
      codedFields.intentionToUseFp,
      member.intentionToUseFpDetail,
    );
    codedFields.healthInsurance = mergeOtherSpecifyDetail(codedFields.healthInsurance, member.healthInsuranceOther);
    codedFields.facilityVisitedPast12Months = mergeOtherSpecifyDetail(
      codedFields.facilityVisitedPast12Months,
      member.facilityVisitedPast12MonthsOther,
    );
    codedFields.healthFacilityVisitReason = mergeOtherSpecifyDetail(
      codedFields.healthFacilityVisitReason,
      member.healthFacilityVisitReasonOther,
    );
    codedFields.reasonLeavingPreviousResidence1 = mergeOtherSpecifyDetail(
      codedFields.reasonLeavingPreviousResidence1,
      member.reasonLeavingPreviousResidence1Other,
    );
    codedFields.reasonLeavingPreviousResidence2 = mergeOtherSpecifyDetail(
      codedFields.reasonLeavingPreviousResidence2,
      member.reasonLeavingPreviousResidence2Other,
    );
    codedFields.reasonLeavingPreviousResidence3 = mergeOtherSpecifyDetail(
      codedFields.reasonLeavingPreviousResidence3,
      member.reasonLeavingPreviousResidence3Other,
    );
    codedFields.returnToPreviousResidence = mergeCodeDetail(
      codedFields.returnToPreviousResidence,
      member.returnToPreviousResidenceDetail,
    );
    codedFields.transferReason1 = mergeOtherSpecifyDetail(codedFields.transferReason1, member.transferReason1Other);
    codedFields.transferReason2 = mergeOtherSpecifyDetail(codedFields.transferReason2, member.transferReason2Other);
    codedFields.transferReason3 = mergeOtherSpecifyDetail(codedFields.transferReason3, member.transferReason3Other);
    codedFields.durationOfStayCurrentBarangay = mergeCodeDetail(
      codedFields.durationOfStayCurrentBarangay,
      member.durationOfStayCurrentBarangayDetail,
    );
    codedFields.skills = mergeOtherSpecifyDetail(codedFields.skills, member.skillsOther);
    const cleanedMember: AddNewDataFormValues["members"][number] = {
      ...cleanStringRecord(member),
      relationshipToHead: cleanCodedDemographicValue(codedFields.relationshipToHead, member.relationshipToHead),
      sex: cleanCodedDemographicValue(codedFields.sex, member.sex),
      dateOfBirth: isReportedAge
        ? ""
        : resolvedBirthdate.compatibilityDate || cleanDemographicDateValue(member, "dateOfBirth", age),
      dateOfBirthUnknown: isReportedAge,
      reportedAge: isReportedAge ? age : null,
      ageAsOfDate: resolvedBirthdate.ageSource
        ? ageReferenceDateValue
        : member.ageAsOfDate ?? null,
      ageSource: resolvedBirthdate.ageSource,
      birthdatePrecision: resolvedBirthdate.precision,
      birthdateOriginalValue: isReportedAge ? "" : resolvedBirthdate.originalValue,
      birthYear: resolvedBirthdate.year,
      birthMonth: resolvedBirthdate.month,
      birthDay: resolvedBirthdate.day,
      importedAge,
      importedAgeAsOf: member.importedAgeAsOf ?? null,
      birthdateSource: resolvedBirthdate.source ?? undefined,
      placeOfBirth: cleanDemographicTextValue(member, "placeOfBirth", age),
      nationality: cleanCodedDemographicValue(codedFields.nationality, member.nationality),
      civilStatus: cleanCodedDemographicValue(codedFields.civilStatus, member.civilStatus),
      religion: cleanDemographicTextValue(member, "religion", age),
      ethnicity: cleanDemographicTextValue(member, "ethnicity", age),
      highestEducation: cleanCodedDemographicValue(codedFields.highestEducation, member.highestEducation),
      currentlyEnrolled: cleanCodedDemographicValue(codedFields.currentlyEnrolled, member.currentlyEnrolled),
      schoolLevel: cleanCodedDemographicValue(codedFields.schoolLevel, member.schoolLevel),
      placeOfSchool: cleanDemographicTextValue(member, "placeOfSchool", age),
      monthlyIncome: cleanIncomeValue(member, age),
      livingChildren: cleanLivingChildrenValue(member, age),
      registeredVoterBarangay: cleanRegisteredVoterBarangayValue(member, age),
    };

    memberCodedFields.forEach(({ field }) => {
      if (isDemographicCodedField(field)) {
        return;
      }

      const result = codedFields[field];
      if (
        field === "reasonNotAttendingSchool" &&
        result &&
        !result.error &&
        result.code === "20" &&
        member.reasonNotAttendingSchoolOther?.trim()
      ) {
        cleanedMember[field] = result.label ?? `Others, ${toProperCase(cleanTypedResponseText(member.reasonNotAttendingSchoolOther))}`;
        return;
      }

      if (field === "skillsTrainingInterest") {
        const fallbackValue = cleanTypedResponseForField(field, member[field]);
        const labelValue = result?.error ? fallbackValue : cleanTypedResponseForField(field, result?.label ?? "");
        cleanedMember[field] = labelValue
          ? labelValue === "None" ? labelValue : toProperCase(labelValue)
          : applicableBlankValue;
        return;
      }

      cleanedMember[field] = result?.error ? cleanPlainText(member[field]) : (result?.label ?? "");
    });

    return {
      ...cleanedMember,
      computedAge: age,
      minimumAgeYears: resolvedBirthdate.minimumAgeYears,
      maximumAgeYears: resolvedBirthdate.maximumAgeYears,
      ageBasis: resolvedBirthdate.ageBasis,
      isAgeApproximate: resolvedBirthdate.isApproximate,
      displayBirthdate: resolvedBirthdate.displayBirthdate,
      codedFields,
    };
  });

  const memberAnswers = members.flatMap((member, index) =>
    buildMemberAnswerRecords(values.members[index], member, index + 1),
  );
  const householdAnswers = buildHouseholdAnswerRecords(values.householdQuestions, householdQuestions);

  const cleaned: CleanedAddNewDataValues = {
    ...values,
    household: cleanHouseholdForReport({ ...values.household, family: familyLetter }),
    interviews: values.interviews.map((interview) => cleanStringRecord(interview)),
    encoding: cleanStringRecord(values.encoding),
    encoded: values,
    householdQuestions,
    members,
    memberAnswers,
    householdAnswers,
    searchText: "",
  };

  cleaned.searchText = buildSearchText(cleaned);

  return cleaned;
}

function shouldPreserveMemberFamilyUnits(values: AddNewDataFormValues, householdFamilyUnitNo: string) {
  const metadata = values as unknown as Record<string, unknown>;
  const sourceSystem = normalizeImportMetadataText(metadata.sourceSystem);
  const importMode = normalizeImportMetadataText(metadata.importMode);
  const hasLegacyMetadata = Boolean(metadata.legacyMigration || metadata.legacyRawData || metadata.legacyExtraFields);

  if (sourceSystem === "old rbim" || importMode === "legacy continuation" || hasLegacyMetadata) {
    return true;
  }

  return values.members.some((member) => {
    const assignment = String(member.familyUnitAssignment ?? "").trim().toLowerCase();
    const memberFamilyUnitNo = normalizeFamilyUnitNo(member.familyUnitNo, householdFamilyUnitNo);

    return assignment !== "" &&
      assignment !== "same" &&
      memberFamilyUnitNo !== householdFamilyUnitNo;
  });
}

function normalizeImportMetadataText(value: unknown) {
  return String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

export function getAddNewDataCodeErrors(values: AddNewDataFormValues) {
  const errors: string[] = [];
  const cleaned = cleanAddNewDataValues(values);

  cleaned.members.forEach((member, index) => {
    memberCodedFields.forEach(({ field }) => {
      const result = member.codedFields[field];

      if (result?.error) {
        errors.push(`Line ${index + 1}: ${result.error}`);
      }
    });
  });

  householdQuestionCodedFields.forEach(({ field }) => {
    const result = cleaned.householdQuestions.codedFields[field];

    if (result?.error) {
      errors.push(result.error);
    }
  });

  return errors;
}

export function getMemberCodedValue(
  member: AddNewDataFormValues["members"][number] | CleanedRbimMember,
  field: MemberFieldName,
) {
  const existing = "codedFields" in member ? member.codedFields[field] : undefined;
  const config = memberCodedFields.find((codedField) => codedField.field === field);
  return existing ?? (config ? cleanRBIMCode(config.questionKey, member[field]) : null);
}

export function getHouseholdQuestionCodedValue(
  householdQuestions: AddNewDataFormValues["householdQuestions"] | CleanedRbimHouseholdQuestions,
  field: HouseholdQuestionFieldName,
) {
  const existing = "codedFields" in householdQuestions ? householdQuestions.codedFields[field] : undefined;
  const config = householdQuestionCodedFields.find((codedField) => codedField.field === field);
  return existing ?? (config ? cleanRBIMCode(config.questionKey, householdQuestions[field]) : null);
}

export function getApplicabilityForQuestion(
  member: AddNewDataFormValues["members"][number],
  field: MemberFieldName,
  age = resolveMemberBirthdate(member).ageYears,
): DemographicApplicability {
  const q12 = cleanRBIMCode("q12_currently_enrolled", member.currentlyEnrolled);
  const sex = cleanRBIMCode("q03_sex", member.sex);
  const residentType = cleanRBIMCode("q37_resident_type", member.residentType);
  const sourceOfIncome = cleanRBIMCode("q17_source_of_income", member.sourceOfIncome);
  const facilityVisited = cleanRBIMCode("q28_facility_visited", member.facilityVisitedPast12Months);

  const isSexKnown = !sex.error && Boolean(sex.code);
  const incomeIsZero = isZeroAmount(member.monthlyIncome);
  const isNonMigrant = !residentType.error && residentType.code === "1";
  const questionCode = memberFieldToQuestionCode[field];

  if (questionCode && isOutsideFinalQuestionRule(questionCode, member, age)) {
    return "notApplicable";
  }

  // Dependency rules below refine the final age/sex/migration buckets.
  if ((field === "schoolLevel" || field === "placeOfSchool") && !q12.error && q12.label === "No") {
    return "notApplicable";
  }

  if (field === "reasonNotAttendingSchool") {
    if (!q12.error && q12.label !== "No") {
      return "notApplicable";
    }
  }

  if ((field === "sourceOfIncome" || field === "workStatus" || field === "placeOfWork") && incomeIsZero) {
    return "notApplicable";
  }

  if (
    (field === "workStatus" || field === "placeOfWork") &&
    !sourceOfIncome.error &&
    ["3", "4", "5"].includes(sourceOfIncome.code)
  ) {
    return "notApplicable";
  }

  if (field === "fpMethodSource") {
    const familyPlanningUse = cleanRBIMCode("q24_family_planning_use", member.familyPlanningUse);

    if (!familyPlanningUse.error && familyPlanningUse.code === "0") {
      return "notApplicable";
    }
  }

  if (field === "healthFacilityVisitReason" && !facilityVisited.error && facilityVisited.code === "0") {
    return "notApplicable";
  }


  if (
    (field === "dateOfTransfer" ||
      field === "reasonLeavingPreviousResidence1" ||
      field === "reasonLeavingPreviousResidence2" ||
      field === "reasonLeavingPreviousResidence3" ||
      field === "returnToPreviousResidence" ||
      field === "transferReason1" ||
      field === "transferReason2" ||
      field === "transferReason3") &&
    isNonMigrant
  ) {
    return "notApplicable";
  }

  return "applicable";
}

function isOutsideFinalQuestionRule(
  questionCode: keyof typeof finalQuestionApplicabilityRules,
  member: AddNewDataFormValues["members"][number],
  age: number | null,
) {
  const rule: FinalQuestionApplicabilityRule = finalQuestionApplicabilityRules[questionCode];

  if (rule.scope !== "member") {
    return false;
  }

  if (age !== null && typeof rule.minAge === "number" && age < rule.minAge) {
    return true;
  }

  if (age !== null && typeof rule.maxAge === "number" && age > rule.maxAge) {
    return true;
  }

  if (rule.sex) {
    const sex = cleanRBIMCode("q03_sex", member.sex);

    if (!sex.error && sex.code && sex.label !== rule.sex) {
      return true;
    }
  }

  if (rule.residentTypes) {
    const residentType = cleanRBIMCode("q37_resident_type", member.residentType);

    if (!residentType.error && residentType.code === "1") {
      return true;
    }
  }

  return false;
}

export const questionCodeToMemberField = {
  Q2: "relationshipToHead",
  Q3: "sex",
  Q5: "dateOfBirth",
  Q6: "placeOfBirth",
  Q7: "nationality",
  Q8: "civilStatus",
  Q9: "religion",
  Q10: "ethnicity",
  Q11: "highestEducation",
  Q12: "currentlyEnrolled",
  Q13: "schoolLevel",
  Q14: "placeOfSchool",
  Q15: "reasonNotAttendingSchool",
  Q16: "monthlyIncome",
  Q17: "sourceOfIncome",
  Q18: "workStatus",
  Q19: "placeOfWork",
  Q20: "placeOfDelivery",
  Q21: "birthAttendant",
  Q22: "immunization",
  Q23: "livingChildren",
  Q24: "familyPlanningUse",
  Q25: "fpMethodSource",
  Q26: "intentionToUseFp",
  Q27: "healthInsurance",
  Q28: "facilityVisitedPast12Months",
  Q29: "healthFacilityVisitReason",
  Q30: "disability",
  Q31: "soloParentStatus",
  Q32: "registeredSeniorCitizen",
  Q33: "registeredVoterBarangay",
  Q34: "lengthOfStayInBarangay",
  Q35: "previousResidenceFiveYears",
  Q36: "previousResidenceSixMonths",
  Q37: "residentType",
  Q38: "dateOfTransfer",
  Q39A: "reasonLeavingPreviousResidence1",
  Q39B: "reasonLeavingPreviousResidence2",
  Q39C: "reasonLeavingPreviousResidence3",
  Q40: "returnToPreviousResidence",
  Q41A: "transferReason1",
  Q41B: "transferReason2",
  Q41C: "transferReason3",
  Q42: "durationOfStayCurrentBarangay",
  Q43A: "hasValidCtc",
  Q43B: "ctcIssuedInBarangay",
  Q44: "skillsTrainingInterest",
  Q45: "skills",
} as const satisfies Record<string, MemberFieldName>;

const memberFieldToQuestionCode = Object.fromEntries(
  Object.entries(questionCodeToMemberField).map(([questionCode, field]) => [field, questionCode]),
) as Partial<Record<MemberFieldName, keyof typeof finalQuestionApplicabilityRules>>;

const questionCodeToQuestionKey = Object.fromEntries(
  memberCodedFields.map(({ field, questionKey }) => [
    Object.entries(questionCodeToMemberField).find(([, mappedField]) => mappedField === field)?.[0],
    questionKey,
  ]),
) as Partial<Record<string, RbimCodeQuestionKey>>;

const memberTextSkipFields = [
  "placeOfSchool",
  "monthlyIncome",
  "livingChildren",
  "livingChildrenAlive",
  "registeredVoterBarangay",
] as const satisfies readonly MemberFieldName[];

export function getAgeApplicability(member: AddNewDataFormValues["members"][number]) {
  const age = resolveMemberBirthdate(member).ageYears;

  return {
    age,
    isInfant: age !== null && age < 1,
    isSchoolAge: age !== null && age >= 3 && age <= 24,
    isWorkingAge: age !== null && age >= 15,
    isReproductiveAge: age !== null && age >= 10 && age <= 54,
    isSenior: age !== null && age >= 60,
  };
}

export function getFinalQuestionApplicabilityRule(questionCode: string) {
  return finalQuestionApplicabilityRules[
    questionCode.toUpperCase() as keyof typeof finalQuestionApplicabilityRules
  ];
}

export function isQuestionApplicable(
  questionCode: string,
  member: AddNewDataFormValues["members"][number],
) {
  const field = questionCodeToMemberField[questionCode.toUpperCase() as keyof typeof questionCodeToMemberField];

  if (!field) {
    return true;
  }

  return getApplicabilityForQuestion(member, field) === "applicable";
}

export function cleanCodeValue(questionCode: string, rawValue: string | undefined) {
  const questionKey = questionCodeToQuestionKey[questionCode.toUpperCase()];

  if (!questionKey) {
    return cleanPlainText(rawValue) || applicableBlankValue;
  }

  const result = cleanRBIMCode(questionKey, rawValue);

  if (result.error) {
    return cleanPlainText(rawValue) || applicableBlankValue;
  }

  return result.label ?? applicableBlankValue;
}

export function applySkipPattern(member: AddNewDataFormValues["members"][number]) {
  const age = resolveMemberBirthdate(member).ageYears;
  const nextMember = { ...member };
  const mutableMember = nextMember as Partial<Record<MemberFieldName, string>>;

  memberCodedFields.forEach(({ field }) => {
    if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
      mutableMember[field] = notApplicableValue;
      return;
    }

    if (shouldClearApplicableSkipValue(field, mutableMember[field])) {
      mutableMember[field] = "";
    }
  });

  memberTextSkipFields.forEach((field) => {
    if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
      mutableMember[field] = notApplicableValue;
      return;
    }

    if (isRBIMNotApplicable(mutableMember[field])) {
      mutableMember[field] = "";
    }
  });

  if (getApplicabilityForQuestion(member, "livingChildren", age) === "applicable" && isZeroAmount(member.livingChildren)) {
    mutableMember.livingChildrenAlive = notApplicableValue;
  }

  return applyDetailSkipPattern(applyEconomicHealthSkipPattern(applyDemographicSkipPattern(nextMember)));
}

export const demographicSkipFields = [
  "highestEducation",
  "currentlyEnrolled",
  "schoolLevel",
  "placeOfSchool",
  "reasonNotAttendingSchool",
] as const satisfies readonly MemberFieldName[];

export const economicHealthSkipFields = [
  "monthlyIncome",
  "sourceOfIncome",
  "workStatus",
  "placeOfWork",
  "placeOfDelivery",
  "birthAttendant",
  "immunization",
  "livingChildren",
  "livingChildrenAlive",
  "familyPlanningUse",
  "fpMethodSource",
  "intentionToUseFp",
] as const satisfies readonly MemberFieldName[];

export const healthMigrationSkipFields = [
  "disability",
  "soloParentStatus",
  "registeredSeniorCitizen",
  "registeredVoterBarangay",
  "lengthOfStayInBarangay",
  "previousResidenceFiveYears",
  "previousResidenceSixMonths",
  "residentType",
] as const satisfies readonly MemberFieldName[];

export const migrationSkillsSkipFields = [
  "dateOfTransfer",
  "reasonLeavingPreviousResidence1",
  "reasonLeavingPreviousResidence2",
  "reasonLeavingPreviousResidence3",
  "returnToPreviousResidence",
  "transferReason1",
  "transferReason2",
  "transferReason3",
  "durationOfStayCurrentBarangay",
  "hasValidCtc",
  "ctcIssuedInBarangay",
  "skillsTrainingInterest",
  "skills",
] as const satisfies readonly MemberFieldName[];

export function applyDemographicSkipPattern(member: AddNewDataFormValues["members"][number]) {
  const age = resolveMemberBirthdate(member).ageYears;
  const nextMember = { ...member };
  const mutableMember = nextMember as Partial<Record<MemberFieldName, string>>;
  const enrollment = cleanRBIMCode("q12_currently_enrolled", member.currentlyEnrolled);
  const reason = cleanRBIMCode("q15_reason_not_attending_school", member.reasonNotAttendingSchool);

  demographicSkipFields.forEach((field) => {
    if (field === "reasonNotAttendingSchool" && !enrollment.code) {
      if (isRBIMNotApplicable(mutableMember[field])) {
        mutableMember[field] = "";
      }
      return;
    }

    if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
      mutableMember[field] = notApplicableValue;
      return;
    }

    if (isRBIMNotApplicable(mutableMember[field])) {
      mutableMember[field] = "";
    }
  });

  if (
    getApplicabilityForQuestion(member, "reasonNotAttendingSchool", age) !== "applicable" ||
    reason.error ||
    reason.code !== "20"
  ) {
    mutableMember.reasonNotAttendingSchoolOther = "";
  }

  return nextMember;
}

export function applyEconomicHealthSkipPattern(member: AddNewDataFormValues["members"][number]) {
  const age = resolveMemberBirthdate(member).ageYears;
  const nextMember = { ...member };
  const mutableMember = nextMember as Partial<Record<MemberFieldName, string>>;
  const fieldsToClearWhenHidden: MemberFieldName[] = [
    "sourceOfIncomeOther",
    "placeOfDeliveryOther",
    "birthAttendantOther",
    "familyPlanningUseOther",
    "fpMethodSourceOther",
    "intentionToUseFpDetail",
  ];
  const sourceOfIncome = cleanRBIMCode("q17_source_of_income", member.sourceOfIncome);
  const placeOfDelivery = cleanRBIMCode("q20_place_of_delivery", member.placeOfDelivery);
  const birthAttendant = cleanRBIMCode("q21_birth_attendant", member.birthAttendant);
  const familyPlanningUse = cleanRBIMCode("q24_family_planning_use", member.familyPlanningUse);
  const fpMethodSource = cleanRBIMCode("q25_fp_method_source", member.fpMethodSource);
  const intention = cleanRBIMCode("q26_intention_to_use_fp", member.intentionToUseFp);

  economicHealthSkipFields.forEach((field) => {
    if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
      mutableMember[field] = notApplicableValue;
      return;
    }

    if (shouldClearApplicableSkipValue(field, mutableMember[field])) {
      mutableMember[field] = "";
    }
  });

  if (getApplicabilityForQuestion(member, "livingChildren", age) === "applicable" && isZeroAmount(member.livingChildren)) {
    mutableMember.livingChildrenAlive = notApplicableValue;
  }

  if (
    getApplicabilityForQuestion(member, "sourceOfIncome", age) !== "applicable" ||
    sourceOfIncome.error ||
    !["1", "2", "3", "4", "5"].includes(sourceOfIncome.code)
  ) {
    mutableMember.sourceOfIncomeOther = "";
  }

  if (getApplicabilityForQuestion(member, "placeOfDelivery", age) !== "applicable" || placeOfDelivery.error || placeOfDelivery.code !== "5") {
    mutableMember.placeOfDeliveryOther = "";
  }

  if (getApplicabilityForQuestion(member, "birthAttendant", age) !== "applicable" || birthAttendant.error || birthAttendant.code !== "5") {
    mutableMember.birthAttendantOther = "";
  }

  if (getApplicabilityForQuestion(member, "familyPlanningUse", age) !== "applicable" || familyPlanningUse.error || familyPlanningUse.code !== "10") {
    mutableMember.familyPlanningUseOther = "";
  }

  if (getApplicabilityForQuestion(member, "fpMethodSource", age) !== "applicable" || fpMethodSource.error || fpMethodSource.code !== "6") {
    mutableMember.fpMethodSourceOther = "";
  }

  if (getApplicabilityForQuestion(member, "intentionToUseFp", age) !== "applicable" || intention.error || !["1", "2"].includes(intention.code)) {
    mutableMember.intentionToUseFpDetail = "";
  }

  fieldsToClearWhenHidden.forEach((field) => {
    if (isRBIMNotApplicable(mutableMember[field])) {
      mutableMember[field] = "";
    }
  });

  return nextMember;
}

function applyDetailSkipPattern(member: AddNewDataFormValues["members"][number]) {
  const age = resolveMemberBirthdate(member).ageYears;
  const nextMember = { ...member };
  const mutableMember = nextMember as Partial<Record<MemberFieldName, string>>;
  const healthInsurance = cleanRBIMCode("q27_health_insurance", member.healthInsurance);
  const facilityVisited = cleanRBIMCode("q28_facility_visited", member.facilityVisitedPast12Months);
  const visitReason = cleanRBIMCode("q29_health_facility_visit_reason", member.healthFacilityVisitReason);
  const reasonLeaving1 = cleanRBIMCode("q39_reason_leaving_previous_residence", member.reasonLeavingPreviousResidence1);
  const reasonLeaving2 = cleanRBIMCode("q39_reason_leaving_previous_residence", member.reasonLeavingPreviousResidence2);
  const reasonLeaving3 = cleanRBIMCode("q39_reason_leaving_previous_residence", member.reasonLeavingPreviousResidence3);
  const returnToPreviousResidence = cleanRBIMCode("q40_return_to_previous_residence", member.returnToPreviousResidence);
  const transferReason1 = cleanRBIMCode("q41_reason_transferring_barangay", member.transferReason1);
  const transferReason2 = cleanRBIMCode("q41_reason_transferring_barangay", member.transferReason2);
  const transferReason3 = cleanRBIMCode("q41_reason_transferring_barangay", member.transferReason3);
  const duration = cleanRBIMCode("q42_duration_of_stay_current_barangay", member.durationOfStayCurrentBarangay);
  const skills = cleanRBIMCode("q45_skills", member.skills);

  if (healthInsurance.error || healthInsurance.code !== "8") {
    mutableMember.healthInsuranceOther = "";
  }

  if (facilityVisited.error || facilityVisited.code !== "8") {
    mutableMember.facilityVisitedPast12MonthsOther = "";
  }

  if (visitReason.error || visitReason.code !== "8") {
    mutableMember.healthFacilityVisitReasonOther = "";
  }

  clearOtherDetailIfHidden(
    mutableMember,
    "reasonLeavingPreviousResidence1Other",
    "reasonLeavingPreviousResidence1",
    reasonLeaving1,
    "15",
    member,
    age,
  );
  clearOtherDetailIfHidden(
    mutableMember,
    "reasonLeavingPreviousResidence2Other",
    "reasonLeavingPreviousResidence2",
    reasonLeaving2,
    "15",
    member,
    age,
  );
  clearOtherDetailIfHidden(
    mutableMember,
    "reasonLeavingPreviousResidence3Other",
    "reasonLeavingPreviousResidence3",
    reasonLeaving3,
    "15",
    member,
    age,
  );

  if (getApplicabilityForQuestion(member, "returnToPreviousResidence", age) !== "applicable") {
    mutableMember.returnToPreviousResidenceDetail = "";
  } else if (!returnToPreviousResidence.error && returnToPreviousResidence.code === "2") {
    mutableMember.returnToPreviousResidenceDetail = notApplicableValue;
  } else if (returnToPreviousResidence.error || returnToPreviousResidence.code !== "1") {
    mutableMember.returnToPreviousResidenceDetail = "";
  } else if (isRBIMNotApplicable(mutableMember.returnToPreviousResidenceDetail)) {
    mutableMember.returnToPreviousResidenceDetail = "";
  }

  clearOtherDetailIfHidden(
    mutableMember,
    "transferReason1Other",
    "transferReason1",
    transferReason1,
    "5",
    member,
    age,
  );
  clearOtherDetailIfHidden(
    mutableMember,
    "transferReason2Other",
    "transferReason2",
    transferReason2,
    "5",
    member,
    age,
  );
  clearOtherDetailIfHidden(
    mutableMember,
    "transferReason3Other",
    "transferReason3",
    transferReason3,
    "5",
    member,
    age,
  );

  if (getApplicabilityForQuestion(member, "durationOfStayCurrentBarangay", age) !== "applicable") {
    mutableMember.durationOfStayCurrentBarangayDetail = "";
  } else if (!duration.error && duration.code === "2") {
    mutableMember.durationOfStayCurrentBarangayDetail = notApplicableValue;
  } else if (duration.error || duration.code !== "1") {
    mutableMember.durationOfStayCurrentBarangayDetail = "";
  } else if (isRBIMNotApplicable(mutableMember.durationOfStayCurrentBarangayDetail)) {
    mutableMember.durationOfStayCurrentBarangayDetail = "";
  }

  if (getApplicabilityForQuestion(member, "skills", age) !== "applicable" || skills.error || skills.code !== "18") {
    mutableMember.skillsOther = "";
  }

  return nextMember;
}

function clearOtherDetailIfHidden(
  mutableMember: Partial<Record<MemberFieldName, string>>,
  detailField: MemberFieldName,
  codedField: MemberFieldName,
  result: RbimCodeCleanResult,
  otherCode: string,
  member: AddNewDataFormValues["members"][number],
  age: number | null,
) {
  if (getApplicabilityForQuestion(member, codedField, age) !== "applicable" || result.error || result.code !== otherCode) {
    mutableMember[detailField] = "";
  }
}

export function cleanDemographicValue(
  member: AddNewDataFormValues["members"][number],
  field: MemberFieldName,
  age = resolveMemberBirthdate(member).ageYears,
) {
  if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
    return notApplicableValue;
  }

  const rawValue = member[field];
  const cleanedValue = cleanTypedResponseForField(field, typeof rawValue === "string" ? rawValue : "");

  return cleanedValue || applicableBlankValue;
}

export function normalizeCodedAnswer(
  member: AddNewDataFormValues["members"][number],
  field: MemberFieldName,
  questionKey: RbimCodeQuestionKey,
  age = resolveMemberBirthdate(member).ageYears,
): RbimCodeCleanResult {
  if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
    return { code: "99", label: notApplicableValue };
  }

  const rawValue = member[field];

  if (typeof rawValue !== "string" || !rawValue.trim()) {
    return { code: applicableBlankValue, label: applicableBlankValue };
  }

  return cleanRBIMCode(questionKey, rawValue);
}

function normalizeHouseholdCodedAnswer(
  questionKey: RbimCodeQuestionKey,
  rawValue: string | undefined,
): RbimCodeCleanResult {
  if (!rawValue?.trim()) {
    return { code: applicableBlankValue, label: applicableBlankValue };
  }

  return cleanRBIMCode(questionKey, rawValue);
}

function resolveMemberBirthdate(member: AddNewDataFormValues["members"][number], referenceDate?: Date) {
  return resolveMemberAge(member as BirthdateResolverInput, referenceDate);
}

function getImportedAgeMetadata(member: AddNewDataFormValues["members"][number]) {
  const legacyMember = member as Record<string, unknown>;
  const candidates = [
    member.importedAge,
    legacyMember.legacyImportedAgeFallback,
    legacyMember.legacyOriginalAge,
    legacyMember.legacyAge,
  ];

  for (const candidate of candidates) {
    const parsedAge = parseAgeCandidate(candidate);

    if (parsedAge !== null) {
      return parsedAge;
    }
  }

  return null;
}

function parseAgeCandidate(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 120) {
    return Math.trunc(value);
  }

  const text = String(value ?? "").trim();

  if (!/^\d{1,3}$/.test(text)) {
    return null;
  }

  const parsedAge = Number(text);

  return Number.isFinite(parsedAge) && parsedAge >= 0 && parsedAge <= 120 ? parsedAge : null;
}

function formatAgeFallbackDisplay(age: number | null) {
  if (age === null) {
    return null;
  }

  return `${age} ${age === 1 ? "year" : "years"}`;
}

function getLegacyRawAgeValue(member: AddNewDataFormValues["members"][number]) {
  const legacyMember = member as Record<string, unknown>;

  return stringifyAnswerValue(legacyMember.legacyOriginalAge ?? legacyMember.legacyAge);
}

function getLegacyAgeRemark(member: AddNewDataFormValues["members"][number]) {
  const resolvedBirthdate = resolveMemberBirthdate(member);
  const precision = resolvedBirthdate.precision;

  if (resolvedBirthdate.ageBasis === "reported") {
    return resolvedBirthdate.ageAsOfDate
      ? `Reported age as of ${resolvedBirthdate.ageAsOfDate}.`
      : "Reported age because Date of Birth is unknown.";
  }

  if (precision === "month-year" || precision === "year-only") {
    return `Auto-computed from partial Q5 Date of Birth (${getBirthdatePrecisionLabel(precision)}).`;
  }

  if (precision === "age-only") {
    return "Imported from the old RBIM Age value because no usable birthdate was available.";
  }

  return null;
}

function buildMemberAnswerRecords(
  rawMember: AddNewDataFormValues["members"][number],
  cleanedMember: CleanedRbimMember,
  lineNumber: number,
): CleanedAnswerRecord[] {
  const resolvedBirthdate = resolveMemberBirthdate(cleanedMember);
  const age = resolvedBirthdate.ageYears;
  const fullNameRaw = formatName(rawMember);
  const fullNameCleaned = formatName(cleanedMember);
  const ageDisplay = resolvedBirthdate.ageDisplay || formatAgeFallbackDisplay(age);
  const legacyAgeRemark = getLegacyAgeRemark(cleanedMember);
  const isReportedAge = resolvedBirthdate.ageBasis === "reported";
  const legacyAgeRawValue = isReportedAge
    ? stringifyAnswerValue(rawMember.reportedAge)
    : getLegacyRawAgeValue(rawMember);

  return [
    makeAnswerRecord({
      questionNumber: "Q1",
      field: "name",
      rawValue: fullNameRaw,
      cleanedValue: fullNameCleaned || applicableBlankValue,
      lineNumber,
    }),
    makeMemberFieldRecord("Q2", "relationshipToHead", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q3", "sex", rawMember, cleanedMember, lineNumber, age),
    makeAnswerRecord({
      questionNumber: "Q4",
      field: "computedAge",
      rawValue: legacyAgeRawValue,
      cleanedValue: ageDisplay ?? applicableBlankValue,
      isAutoFilled: Boolean(ageDisplay) && !isReportedAge,
      lineNumber,
      remarks: legacyAgeRemark ?? "Auto-computed from Q5 Date of Birth.",
    }),
    isReportedAge
      ? makeAnswerRecord({
          questionNumber: "Q5",
          field: "dateOfBirth",
          rawValue: "",
          cleanedValue: "Unknown",
          lineNumber,
          remarks: "Date of Birth Unknown selected; no date was fabricated.",
        })
      : makeMemberFieldRecord("Q5", "dateOfBirth", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q6", "placeOfBirth", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q7", "nationality", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q8", "civilStatus", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q9", "religion", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q10", "ethnicity", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q11", "highestEducation", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q12", "currentlyEnrolled", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q13", "schoolLevel", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q14", "placeOfSchool", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q15", "reasonNotAttendingSchool", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q16", "monthlyIncome", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q17", "sourceOfIncome", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q18", "workStatus", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q19", "placeOfWork", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q20", "placeOfDelivery", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q21", "birthAttendant", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q22", "immunization", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q23", "livingChildren", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q24", "familyPlanningUse", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q25", "fpMethodSource", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q26", "intentionToUseFp", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q27", "healthInsurance", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q28", "facilityVisitedPast12Months", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q29", "healthFacilityVisitReason", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q30", "disability", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q31", "soloParentStatus", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q32", "registeredSeniorCitizen", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q33", "registeredVoterBarangay", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q34", "lengthOfStayInBarangay", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q35", "previousResidenceFiveYears", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q36", "previousResidenceSixMonths", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q37", "residentType", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q38", "dateOfTransfer", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q39A", "reasonLeavingPreviousResidence1", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q39B", "reasonLeavingPreviousResidence2", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q39C", "reasonLeavingPreviousResidence3", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q40", "returnToPreviousResidence", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q41A", "transferReason1", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q41B", "transferReason2", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q41C", "transferReason3", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q42", "durationOfStayCurrentBarangay", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q43A", "hasValidCtc", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q43B", "ctcIssuedInBarangay", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q44", "skillsTrainingInterest", rawMember, cleanedMember, lineNumber, age),
    makeMemberFieldRecord("Q45", "skills", rawMember, cleanedMember, lineNumber, age),
  ];
}

function makeMemberFieldRecord(
  questionNumber: string,
  field: MemberFieldName,
  rawMember: AddNewDataFormValues["members"][number],
  cleanedMember: CleanedRbimMember,
  lineNumber: number,
  age: number | null,
) {
  const cleanedValue = stringifyAnswerValue(cleanedMember[field]) || applicableBlankValue;
  const codedResult = cleanedMember.codedFields[field];
  const isApplicable = getApplicabilityForQuestion(rawMember, field, age) === "applicable";
  const rawValue = !isApplicable
    ? "99"
    : field === "nationality" && rawMember.nationalityOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.nationalityOther])
    : field === "reasonNotAttendingSchool" && rawMember.reasonNotAttendingSchoolOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.reasonNotAttendingSchoolOther])
    : field === "sourceOfIncome" && rawMember.sourceOfIncomeOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.sourceOfIncomeOther])
    : field === "placeOfDelivery" && rawMember.placeOfDeliveryOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.placeOfDeliveryOther])
    : field === "birthAttendant" && rawMember.birthAttendantOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.birthAttendantOther])
    : field === "familyPlanningUse" && rawMember.familyPlanningUseOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.familyPlanningUseOther])
    : field === "fpMethodSource" && rawMember.fpMethodSourceOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.fpMethodSourceOther])
    : field === "intentionToUseFp" && rawMember.intentionToUseFpDetail?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.intentionToUseFpDetail])
    : field === "livingChildren" && rawMember.livingChildrenAlive?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.livingChildrenAlive])
    : field === "healthInsurance" && rawMember.healthInsuranceOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.healthInsuranceOther])
    : field === "facilityVisitedPast12Months" && rawMember.facilityVisitedPast12MonthsOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.facilityVisitedPast12MonthsOther])
    : field === "healthFacilityVisitReason" && rawMember.healthFacilityVisitReasonOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.healthFacilityVisitReasonOther])
    : field === "reasonLeavingPreviousResidence1" && rawMember.reasonLeavingPreviousResidence1Other?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.reasonLeavingPreviousResidence1Other])
    : field === "reasonLeavingPreviousResidence2" && rawMember.reasonLeavingPreviousResidence2Other?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.reasonLeavingPreviousResidence2Other])
    : field === "reasonLeavingPreviousResidence3" && rawMember.reasonLeavingPreviousResidence3Other?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.reasonLeavingPreviousResidence3Other])
    : field === "returnToPreviousResidence" && rawMember.returnToPreviousResidenceDetail?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.returnToPreviousResidenceDetail])
    : field === "transferReason1" && rawMember.transferReason1Other?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.transferReason1Other])
    : field === "transferReason2" && rawMember.transferReason2Other?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.transferReason2Other])
    : field === "transferReason3" && rawMember.transferReason3Other?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.transferReason3Other])
    : field === "durationOfStayCurrentBarangay" && rawMember.durationOfStayCurrentBarangayDetail?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.durationOfStayCurrentBarangayDetail])
    : field === "skills" && rawMember.skillsOther?.trim()
      ? joinAnswerValues([rawMember[field], rawMember.skillsOther])
      : stringifyAnswerValue(rawMember[field]);

  return makeAnswerRecord({
    questionNumber,
    field,
    rawValue,
    cleanedValue,
    isApplicable,
    isAutoFilled: !isApplicable || cleanedValue === notApplicableValue,
    lineNumber,
    validationStatus: codedResult?.error ? "invalid" : "valid",
    remarks: codedResult?.error,
  });
}

function buildHouseholdAnswerRecords(
  rawQuestions: AddNewDataFormValues["householdQuestions"],
  cleanedQuestions: CleanedRbimHouseholdQuestions,
): CleanedAnswerRecord[] {
  return [
    makeHouseholdFieldRecord("Q46", "housingTenure", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q47", "lotTenure", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q48", "hazardProneArea", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q49", "disasterExperiencedPastFiveYears", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q50", "knowsNearestEvacuationCenter", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q51", "lightingFuel", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q52", "cookingFuel", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q53", "drinkingWaterSource", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q54a", "garbageDisposal", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q54b", "segregatesGarbage", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q55", "toiletFacility", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q56", "buildingType", rawQuestions, cleanedQuestions),
    makeHouseholdFieldRecord("Q57", "wallMaterial", rawQuestions, cleanedQuestions),
    makeCompoundHouseholdRecord("Q58", "femaleDeathPast12Months", ["femaleDeathAge", "femaleDeathCause"], rawQuestions, cleanedQuestions),
    makeCompoundHouseholdRecord("Q59", "childDeathPast12Months", ["childDeathAge", "childDeathSex", "childDeathCause"], rawQuestions, cleanedQuestions),
    makeCompoundHouseholdRecord("Q60", "commonDiseaseCause1", ["commonDiseaseCause2", "commonDiseaseCause3"], rawQuestions, cleanedQuestions),
    makeCompoundHouseholdRecord("Q61", "primaryNeed1", ["primaryNeed2", "primaryNeed3"], rawQuestions, cleanedQuestions),
    makeCompoundHouseholdRecord("Q62", "futureBarangay", ["futureMunicipality", "futureProvince"], rawQuestions, cleanedQuestions),
  ];
}

function makeHouseholdFieldRecord(
  questionNumber: string,
  field: HouseholdQuestionFieldName,
  rawQuestions: AddNewDataFormValues["householdQuestions"],
  cleanedQuestions: CleanedRbimHouseholdQuestions,
) {
  const rawValue = stringifyAnswerValue(rawQuestions[field]);
  const codedResult = cleanedQuestions.codedFields[field];
  const cleanedValue =
    stringifyAnswerValue(cleanedQuestions[field]) || codedResult?.label || applicableBlankValue;

  return makeAnswerRecord({
    questionNumber,
    field,
    rawValue,
    cleanedValue,
    validationStatus: codedResult?.error ? "invalid" : "valid",
    remarks: codedResult?.error,
  });
}

function makeCompoundHouseholdRecord(
  questionNumber: string,
  primaryField: HouseholdQuestionFieldName,
  detailFields: HouseholdQuestionFieldName[],
  rawQuestions: AddNewDataFormValues["householdQuestions"],
  cleanedQuestions: CleanedRbimHouseholdQuestions,
) {
  const fields = [primaryField, ...detailFields];
  const rawValue = joinAnswerValues(fields.map((field) => rawQuestions[field]));
  const cleanedValue = joinAnswerValues(fields.map((field) => cleanedQuestions[field]));
  const codedResult = cleanedQuestions.codedFields[primaryField];

  return makeAnswerRecord({
    questionNumber,
    field: fields.join(","),
    rawValue,
    cleanedValue,
    validationStatus: codedResult?.error ? "invalid" : "valid",
    remarks: codedResult?.error,
  });
}

function makeAnswerRecord({
  questionNumber,
  field,
  rawValue,
  cleanedValue,
  isApplicable = true,
  isAutoFilled,
  validationStatus = "valid",
  remarks,
  lineNumber,
}: {
  questionNumber: string;
  field: string;
  rawValue: string;
  cleanedValue: string;
  isApplicable?: boolean;
  isAutoFilled?: boolean;
  validationStatus?: CleanedAnswerRecord["validation_status"];
  remarks?: string;
  lineNumber?: number;
}): CleanedAnswerRecord {
  return {
    question_number: questionNumber,
    field,
    raw_value: rawValue,
    cleaned_value: cleanedValue || applicableBlankValue,
    is_applicable: isApplicable,
    is_auto_filled: isAutoFilled ?? (!rawValue && Boolean(cleanedValue)),
    validation_status: validationStatus,
    remarks,
    line_number: lineNumber,
  };
}

function formatName(member: Pick<AddNewDataFormValues["members"][number], "firstName" | "middleName" | "lastName">) {
  return [member.lastName, member.firstName, member.middleName].filter(Boolean).join(", ");
}

function joinAnswerValues(values: Array<string | undefined>) {
  return values.map(stringifyAnswerValue).filter(Boolean).join(" / ") || applicableBlankValue;
}

function stringifyAnswerValue(value: unknown) {
  return typeof value === "string" ? cleanPlainText(value) : String(value ?? "").trim();
}

function buildSearchText(values: CleanedAddNewDataValues) {
  const parts: string[] = [
    values.household.province,
    values.household.municipality,
    values.household.barangay,
    values.household.address,
    values.household.householdCode,
    values.household.respondentName,
    values.household.householdHead,
  ];

  values.members.forEach((member) => {
    parts.push(member.firstName ?? "", member.middleName ?? "", member.lastName ?? "", member.placeOfBirth ?? "");
    Object.entries(member).forEach(([key, value]) => {
      if (key === "codedFields" || typeof value !== "string") {
        return;
      }

      parts.push(value);
    });
    Object.values(member.codedFields).forEach((result) => {
      parts.push(result?.code ?? "", result?.label ?? "");
    });
  });

  Object.values(values.householdQuestions.codedFields).forEach((result) => {
    parts.push(result?.code ?? "", result?.label ?? "");
  });

  return parts.filter(Boolean).join(" ").toUpperCase();
}

function cleanStringRecord<T extends Record<string, unknown>>(record: T): T {
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      typeof value === "string" ? cleanPlainText(value) : value,
    ]),
  ) as T;
}

function cleanHouseholdForReport(household: AddNewDataFormValues["household"]) {
  const cleanedHousehold = cleanStringRecord(household);
  const rbimDisplayCodes = parseRbimDisplayCodes(cleanedHousehold.barangayCode);

  if (!rbimDisplayCodes.barangayCode) {
    return cleanedHousehold;
  }

  return {
    ...cleanedHousehold,
    provinceCode: rbimDisplayCodes.provinceOrHucCode,
    municipalityCode: rbimDisplayCodes.municipalityCityCode,
    barangayCode: rbimDisplayCodes.barangayCode,
  };
}

function isDemographicCodedField(field: MemberFieldName) {
  return (
    field === "relationshipToHead" ||
    field === "sex" ||
    field === "nationality" ||
    field === "civilStatus" ||
    field === "highestEducation" ||
    field === "currentlyEnrolled" ||
    field === "schoolLevel"
  );
}

function isZeroAmount(value: string | undefined) {
  const normalizedValue = String(value ?? "").replace(/,/g, "").trim();

  if (/^(NONE|NO INCOME|N\/A|NA)$/i.test(normalizedValue)) {
    return true;
  }

  return normalizedValue.length > 0 && Number(normalizedValue) === 0;
}

function cleanIncomeValue(member: AddNewDataFormValues["members"][number], age: number | null) {
  if (getApplicabilityForQuestion(member, "monthlyIncome", age) === "notApplicable") {
    return notApplicableValue;
  }

  const rawValue = String(member.monthlyIncome ?? "").replace(/,/g, "").trim();

  if (!rawValue) {
    return applicableBlankValue;
  }

  if (/^(NONE|NO INCOME|N\/A|NA)$/i.test(rawValue)) {
    return "0";
  }

  const numericValue = Number(rawValue);

  if (!Number.isFinite(numericValue)) {
    return cleanDemographicPlainText(member.monthlyIncome) || applicableBlankValue;
  }

  return numericValue.toLocaleString("en-US");
}

function cleanLivingChildrenValue(member: AddNewDataFormValues["members"][number], age: number | null) {
  if (getApplicabilityForQuestion(member, "livingChildren", age) === "notApplicable") {
    return notApplicableValue;
  }

  const totalPregnancies = cleanDemographicPlainText(member.livingChildren);
  const livingChildren = cleanDemographicPlainText(member.livingChildrenAlive);

  if (!totalPregnancies && !livingChildren) {
    return applicableBlankValue;
  }

  if (isZeroAmount(totalPregnancies)) {
    return "0 / 99 - N/A";
  }

  return [totalPregnancies || applicableBlankValue, livingChildren || applicableBlankValue].join(" / ");
}

function cleanCodedDemographicValue(result: RbimCodeCleanResult | undefined, fallback: string | undefined) {
  if (!result) {
    return cleanDemographicPlainText(fallback) || applicableBlankValue;
  }

  if (result.error) {
    return cleanDemographicPlainText(fallback) || applicableBlankValue;
  }

  return result.label ?? applicableBlankValue;
}

function mergeNationalityDetail(
  result: RbimCodeCleanResult | undefined,
  detail: string | undefined,
): RbimCodeCleanResult | undefined {
  if (!result || result.error || result.code !== "2" || result.label?.includes(",")) {
    return result;
  }

  const cleanedDetail = cleanDemographicPlainText(detail);

  if (!cleanedDetail) {
    return result;
  }

  return {
    ...result,
    label: `Non-Filipino, ${toProperCase(cleanedDetail)}`,
  };
}

function mergeOtherSpecifyDetail(
  result: RbimCodeCleanResult | undefined,
  detail: string | undefined,
): RbimCodeCleanResult | undefined {
  if (!result || result.error || result.label?.includes(",") || !/^others\b/i.test(result.label ?? "")) {
    return result;
  }

  const cleanedDetail = cleanTypedResponseText(detail);

  if (!cleanedDetail) {
    return result;
  }

  return {
    ...result,
    label: `Others, ${toProperCase(cleanedDetail)}`,
  };
}

function mergeIntentionToUseFpDetail(
  result: RbimCodeCleanResult | undefined,
  detail: string | undefined,
): RbimCodeCleanResult | undefined {
  if (!result || result.error || !["1", "2"].includes(result.code)) {
    return result;
  }

  const cleanedDetail =
    result.code === "1"
      ? cleanFpMethodDetail(detail)
      : cleanTypedResponseText(detail);

  if (!cleanedDetail) {
    return result;
  }

  return {
    ...result,
    label: result.code === "1"
      ? `${result.label}, ${cleanedDetail}`
      : `${result.label}, ${toProperCase(cleanedDetail)}`,
  };
}

function mergeCodeDetail(
  result: RbimCodeCleanResult | undefined,
  detail: string | undefined,
): RbimCodeCleanResult | undefined {
  if (!result || result.error || !result.label || result.label === notApplicableValue || result.label?.includes(",")) {
    return result;
  }

  const cleanedDetail = cleanTypedResponseText(detail);

  if (!cleanedDetail || isRBIMNotApplicable(cleanedDetail)) {
    return result;
  }

  return {
    ...result,
    label: `${result.label}, ${toProperCase(cleanedDetail)}`,
  };
}

function cleanDemographicDateValue(
  member: AddNewDataFormValues["members"][number],
  field: MemberFieldName,
  age: number | null,
) {
  if (getApplicabilityForQuestion(member, field, age) === "notApplicable") {
    return notApplicableValue;
  }

  const cleanedValue = cleanDemographicPlainText(member.dateOfBirth);

  if (!cleanedValue) {
    return applicableBlankValue;
  }

  return normalizeBirthdateForStorage(cleanedValue);
}

function cleanDemographicTextValue(
  member: AddNewDataFormValues["members"][number],
  field: MemberFieldName,
  age: number | null,
) {
  const cleanedValue = cleanDemographicValue(member, field, age);

  if (cleanedValue === applicableBlankValue || cleanedValue === notApplicableValue) {
    return cleanedValue;
  }

  if (field === "religion" || field === "ethnicity" || cleanedValue === "None") {
    return cleanedValue;
  }

  return toProperCase(cleanedValue);
}

function cleanPlainText(value: string | undefined) {
  return cleanSpecialResponseText(value).replace(/\s+/g, " ").trim();
}

function cleanDemographicPlainText(value: string | undefined) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function cleanTypedResponseForField(field: MemberFieldName, value: string | undefined) {
  const cleanedValue = cleanDemographicPlainText(value);

  if (!cleanedValue) {
    return "";
  }

  if (field === "skillsTrainingInterest" && isNoneText(cleanedValue)) {
    return "None";
  }

  if (field === "religion") {
    return standardizeReligion(cleanedValue) ?? applyEnglishTextCorrections(cleanedValue);
  }

  if (field === "ethnicity") {
    return standardizeEthnicity(cleanedValue) ?? cleanedValue;
  }

  return applyEnglishTextCorrections(cleanedValue);
}

function cleanTypedResponseText(value: string | undefined) {
  const cleanedValue = cleanDemographicPlainText(value);

  if (!cleanedValue) {
    return "";
  }

  if (isNoneText(cleanedValue)) {
    return "None";
  }

  return applyEnglishTextCorrections(cleanedValue);
}

function cleanFpMethodDetail(value: string | undefined) {
  const cleanedValue = cleanTypedResponseText(value);

  if (!cleanedValue) {
    return "";
  }

  const result = cleanRBIMCode("q26_intended_fp_method", cleanedValue);

  if (result.error) {
    return cleanedValue;
  }

  return result.label ?? cleanedValue;
}

function cleanFreeText(value: string | undefined) {
  const cleanedValue = cleanPlainText(value);

  if (isCleanSpecialResponse(cleanedValue)) {
    return cleanedValue;
  }

  return toProperCase(cleanedValue);
}

function cleanSpecialResponseText(value: string | undefined) {
  const cleanedValue = String(value ?? "").replace(/\s+/g, " ").trim();
  const normalizedValue = cleanedValue.toUpperCase().replace(/['’]/g, "");

  if (
    normalizedValue === "99" ||
    normalizedValue.startsWith("99 -") ||
    normalizedValue === "N/A" ||
    normalizedValue === "NA" ||
    normalizedValue === "NOT APPLICABLE"
  ) {
    return notApplicableValue;
  }

  return cleanedValue;
}

function isCleanSpecialResponse(value: string) {
  return value === notApplicableValue;
}

function isNoneText(value: string) {
  const normalizedValue = normalizeStandardKey(value);

  return normalizedValue === "0" || normalizedValue === "NONE" || normalizedValue === "0 NONE";
}

function applyEnglishTextCorrections(value: string) {
  return value.replace(/[A-Za-z]+(?:['-][A-Za-z]+)*/g, (word) => {
    const normalizedWord = normalizeStandardKey(word);

    if (bisayaWords.has(normalizedWord)) {
      return word;
    }

    return englishCorrections[normalizedWord] ?? word;
  });
}

function standardizeReligion(value: string) {
  return religionStandardizationMap.get(normalizeStandardKey(value)) ?? null;
}

function standardizeEthnicity(value: string) {
  return ethnicityStandardizationMap.get(normalizeStandardKey(value)) ?? null;
}

function normalizeStandardKey(value: string) {
  return value
    .trim()
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/[’']/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildStandardizationMap(entries: Array<[string, ...string[]]>) {
  const map = new Map<string, string>();

  entries.forEach(([label, ...aliases]) => {
    [label, ...aliases].forEach((alias) => {
      map.set(normalizeStandardKey(alias), label);
    });
  });

  return map;
}

const englishCorrections: Record<string, string> = {
  BRGY: "barangay",
  GOVERMENT: "government",
  HOSPITEL: "hospital",
  MENOPOS: "menopause",
  MENOPOSE: "menopause",
  MINOPOSE: "menopause",
  PREGNACY: "pregnancy",
  PREGNANTCY: "pregnancy",
  STATON: "station",
};

const bisayaWords = new Set([
  "ANAK",
  "ASAWA",
  "BALAY",
  "BANA",
  "BARANGAY",
  "DILI",
  "GIKAN",
  "GINIKANAN",
  "IGSOON",
  "KAAYO",
  "LOLA",
  "LOLO",
  "NAA",
  "PUROK",
  "SITIO",
  "TRABAHO",
  "UBAN",
  "WALA",
]);

const religionStandardizationMap = buildStandardizationMap([
  ["Assemblies of God", "AG"],
  ["Aglipay"],
  ["Christian and Missionary Alliance Church of the Philippines", "CMACP"],
  ["Alliance of Bible Christian Communities of the Philippines", "ABCCP"],
  ["Association of Fundamental Baptist Churches in the Philippines", "Baptist - AFBCP", "AFBCP"],
  ["Baptist Conference of the Philippines", "Baptist - BCP"],
  ["General Baptist Churches of the Philippines", "Baptist - GBCP", "GBCP"],
  ["Southern Baptist Church", "Baptist - SBC", "SBC"],
  ["Independent Baptist Churches of the Philippines", "Baptist - IBCP", "IBCP"],
  ["Faith Baptist Church", "Baptist - FBC"],
  ["Conservative Baptist Association in the Philippines", "Baptist - CBAP", "CBAP"],
  ["Convention of the Philippine Baptist Church", "Baptist - CPBC", "CPBC"],
  ["Bible Baptist Church", "Baptist - BBC", "BBC", "Bible Baptist"],
  ["Missionary Baptist Churches of the Philippines", "Baptist - MBCP", "MBCP"],
  ["Other Baptists", "Other Baptist"],
  ["Iglesia Filipina Independiente", "IFI"],
  ["Iglesia ni Cristo", "INC"],
  ["Islam"],
  ["Roman Catholic, excluding Catholic Charismatics", "RC", "Roman Catholic", "Catholic"],
  ["Seventh Day Adventist", "SDA"],
  ["United Church of Christ in the Philippines", "UCCP"],
  ["Pentecostal Church of God Asia Mission", "Pentecostal - PCGAM", "PCGAM"],
  ["Philippine Pentecostal Holiness Church", "Pentecostal - PPHC", "PPHC"],
  ["United Pentecostal Church (Philippines), Inc.", "Pentecostal - UPCPI", "UPCPI"],
  ["Universal Pentecostal Church", "Pentecostal - UPC", "UPC"],
  ["Bethany Church of Philippines", "BCP"],
  ["Bible Centered Fellowship", "BCF"],
  ["Buddhist"],
  ["Charismatic Full Gospel Ministries", "CFGM"],
  ["Christ Faith Fellowship Philippines, Inc.", "CFFPI"],
  ["Christ the Living Stone Fellowship", "CLSF"],
  ["Christian Reformed Church in the Philippines, Incorporated", "CRCPI"],
  ["Christ's Commission Fellowship", "CCF"],
  ["Church of Christ", "COC"],
  ["Church of God World Missions in the Philippines", "CGWMP"],
  ["Church of Jesus Christ of the Latter Day Saints", "CJCJLDS", "LDS"],
  ["Church of the Foursquare Gospel in the Philippines, Incorporated", "CFGPI"],
  ["Crusaders of the Divine Church of Christ, Incorporated", "CDCCI"],
  ["Episcopal Church in the Philippines", "ECP"],
  ["Evangelical Free Church of the Philippines", "EFCP"],
  ["Evangelical Presbyterian Church", "EPC"],
  ["Free Believers in Christ Fellowship", "FBCF"],
  ["Free Methodist Church", "Methodist - FMC", "FMC"],
  ["Fundamental Grace Gospel Church of the Christ in the Philippines, Inc.", "FGGCP CI"],
  ["Iglesia sa Dios Espiritu Santo, Incorporated", "IDESI"],
  ["International Baptist Missionary Fellowship", "IBMF"],
  ["Jehovah's Witness", "JW", "Jehovah Witness"],
  ["Jesus Christ Saves Global Outreach", "JCSGO"],
  ["Jesus is Alive Community, Inc.", "JACI"],
  ["Jesus is Lord Church", "JIL"],
  ["Jesus Reigns Ministries", "Jesus Reigns"],
  ["Lord Jesus Our Redeemer Church Foundation International, Inc.", "LJRCFII"],
  ["Lutheran Church of the Philippines", "LCP"],
  ["Miracle Revival Church of the Philippines", "MRCP"],
  ["Other Protestants"],
  ["Philippine Benevolent Missionaries Association", "PBMA"],
  ["Philippine Ecumenical Christian Church", "PECC"],
  ["Philippines General Council of the Assemblies of God", "PGCAG"],
  ["Presbyterian Church in the Philippines", "PCP"],
  ["Tribal Religion"],
  ["United Evangelical Church of the Philippines", "UECP"],
  ["United Methodists Church", "UMC", "United Methodist Church"],
  ["Victory Chapel Christian Fellowship", "VCCF"],
  ["Victory Christian Fellowship of the Philippines, Inc.", "VCFPI"],
  ["Wesleyan Church"],
  ["Zion Christian Community Church", "ZCCC"],
]);

const ethnicityStandardizationMap = buildStandardizationMap([
  ["Aeta"],
  ["Badjao"],
  ["Bagobo"],
  ["Bicol", "Bikol"],
  ["Bisaya", "Visaya"],
  ["Boholano"],
  ["Bontok (Mountain Province)", "Bontok"],
  ["Bukidnon"],
  ["Higa-onon", "Higaonon"],
  ["Manobo"],
  ["Matigsalug"],
  ["Talaandig"],
  ["Tigwahanon"],
  ["Umayamnon"],
  ["Cagaynen (Palawan Cagayan Island)", "Cagaynen"],
  ["Capizeno", "Capizeno"],
  ["Caviteno"],
  ["Caviteno-Chavacano", "Caviteno Chavacano"],
  ["Cebuano"],
  ["Cotabateno"],
  ["Cotabateno-Chavacano", "Cotabateno Chavacano"],
  ["Davao-Chavacano", "Davao Chavacano"],
  ["Davaweno"],
  ["Dumagat"],
  ["Ibaloi (Igorot)", "Ibaloi", "Igorot"],
  ["Ifugao"],
  ["Ilocano"],
  ["Ilonggo"],
  ["Ivatan (Batanes)", "Ivatan"],
  ["Kalinga"],
  ["Kamiguin"],
  ["Kapampangan"],
  ["Karay-a", "Karay a"],
  ["Magahats"],
  ["Maguindanao"],
  ["Mandaya"],
  ["Maranao"],
  ["Masbateno"],
  ["Palawani"],
  ["Pangasinan"],
  ["Romblomanon"],
  ["Subanon"],
  ["Surigaonon"],
  ["T'boli / Tboli", "Tboli", "T boli"],
  ["Tagalog"],
  ["Tausug"],
  ["Waray"],
  ["Zamageno-Chavacano", "Zamageno Chavacano"],
]);

function toProperCase(value: string) {
  const smallWords = new Set(["and", "at", "de", "del", "ng", "ni", "of", "sa", "the"]);

  return value
    .toLowerCase()
    .replace(/[a-z]+(?:['-][a-z]+)*/g, (word, offset) => {
      if (offset > 0 && smallWords.has(word)) {
        return word;
      }

      return word
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("-");
    });
}
